import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col } from "@/Components/ui";
;
import { Plus, X, Upload, GripVertical, Trash2, Image as ImageIcon } from "lucide-react";
import toast from "react-hot-toast";
import { API, BACKEND_HOST } from "../../../api";
import { dialogService } from "@/utils/dialogService";
interface FacilityImage {
    id: number;
    image: string;
    alt_text?: string;
    display_order: number;
}
interface Facility {
    id?: number;
    name: string;
    description: string;
    image?: string | null;
    include_info: string[];
    using_info: string[];
    caution_info: string[];
    is_active: boolean;
    detail_images?: FacilityImage[];
}
interface FacilityModalProps {
    isOpen: boolean;
    toggle: () => void;
    facility: Facility | null;
    onSave: (facility: Facility) => void;
    siteId: string | number;
    isCopyMode?: boolean;
}
const FacilityModal: React.FC<FacilityModalProps> = ({ isOpen, toggle, facility, onSave, siteId, isCopyMode = false, }) => {
    const [formData, setFormData] = useState<Facility>({
        name: "",
        description: "",
        image: null,
        include_info: [],
        using_info: [],
        caution_info: [],
        is_active: true,
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [detailImages, setDetailImages] = useState<FacilityImage[]>([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    useEffect(() => {
        if (facility) {
            setFormData({
                ...facility,
                name: isCopyMode ? facility.name + " (복사)" : facility.name,
                include_info: Array.isArray(facility.include_info) ? facility.include_info : [],
                using_info: Array.isArray(facility.using_info) ? facility.using_info : [],
                caution_info: Array.isArray(facility.caution_info) ? facility.caution_info : [],
            });
            if (!isCopyMode) {
                setImagePreview(facility.image || null);
                setDetailImages(facility.detail_images || []);
            }
            else {
                setImagePreview(null);
                setDetailImages([]);
            }
        }
        else {
            setFormData({
                name: "",
                description: "",
                image: null,
                include_info: [],
                using_info: [],
                caution_info: [],
                is_active: true,
            });
            setImagePreview(null);
            setDetailImages([]);
        }
        setImageFile(null);
    }, [facility, isOpen, isCopyMode]);
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };
    const handleArrayChange = (field: "include_info" | "using_info" | "caution_info", index: number, value: string) => {
        const newArray = [...formData[field]];
        newArray[index] = value;
        setFormData((prev) => ({ ...prev, [field]: newArray }));
    };
    const addArrayItem = (field: "include_info" | "using_info" | "caution_info") => {
        setFormData((prev) => ({ ...prev, [field]: [...prev[field], ""] }));
    };
    const removeArrayItem = (field: "include_info" | "using_info" | "caution_info", index: number) => {
        const newArray = [...formData[field]];
        newArray.splice(index, 1);
        setFormData((prev) => ({ ...prev, [field]: newArray }));
    };
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };
    // Detail images handlers
    const handleDetailImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!facility?.id) {
            toast.error("시설을 먼저 저장한 후 상세 이미지를 추가하세요.");
            return;
        }
        if (!e.target.files || e.target.files.length === 0)
            return;
        setUploadingImages(true);
        const files = Array.from(e.target.files);
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append("facility", facility.id.toString());
                formData.append("image", file);
                formData.append("display_order", detailImages.length.toString());
                const response = await API.post(`${BACKEND_HOST}/sites/facility-images/`, formData);
                setDetailImages(prev => [...prev, response.data]);
            }
            toast.success(`${files.length}개 이미지가 추가되었습니다.`);
        }
        catch (error) {
            console.error("Upload detail images error:", error);
            toast.error("이미지 업로드 중 오류가 발생했습니다.");
        }
        finally {
            setUploadingImages(false);
            e.target.value = "";
        }
    };
    const handleDeleteDetailImage = async (imageId: number) => {
        if (!await dialogService.confirm("이 이미지를 삭제하시겠습니까?"))
            return;
        try {
            await API.delete(`/sites/facility-images/${imageId}/`);
            setDetailImages(prev => prev.filter(img => img.id !== imageId));
            toast.success("이미지가 삭제되었습니다.");
        }
        catch (error) {
            console.error("Delete detail image error:", error);
            toast.error("이미지 삭제 중 오류가 발생했습니다.");
        }
    };
    const handleDeleteMainImage = async () => {
        if (!await dialogService.confirm("대표 이미지를 삭제하시겠습니까?"))
            return;
        setImagePreview(null);
        setImageFile(null);
        setFormData(prev => ({ ...prev, image: null }));
    };
    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };
    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index)
            return;
        const newImages = [...detailImages];
        const [draggedItem] = newImages.splice(draggedIndex, 1);
        newImages.splice(index, 0, draggedItem);
        setDetailImages(newImages);
        setDraggedIndex(index);
    };
    const handleDragEnd = () => {
        setDraggedIndex(null);
    };
    const handleSaveDetailImageOrder = async () => {
        try {
            const orderData = {
                images: detailImages.map((img, index) => ({
                    id: img.id,
                    display_order: index,
                })),
            };
            await API.put(`${BACKEND_HOST}/sites/facility-images/update_order/`, orderData);
            toast.success("이미지 순서가 저장되었습니다.");
        }
        catch (error) {
            console.error("Update image order error:", error);
            toast.error("순서 저장 중 오류가 발생했습니다.");
        }
    };
    const handleSave = async () => {
        if (!formData.name) {
            toast.error("시설 이름을 입력해주세요.");
            return;
        }
        setIsSaving(true);
        try {
            const data = new FormData();
            data.append("site", siteId.toString());
            data.append("name", formData.name);
            data.append("description", formData.description);
            data.append("is_active", String(formData.is_active));
            data.append("include_info", JSON.stringify(formData.include_info));
            data.append("using_info", JSON.stringify(formData.using_info));
            data.append("caution_info", JSON.stringify(formData.caution_info));
            if (imageFile) {
                data.append("image", imageFile);
            }
            else if (imagePreview === null) {
                data.append("image", "");
            }
            let response;
            if (facility?.id && !isCopyMode) {
                response = await API.patch(`${BACKEND_HOST}/sites/facilities/${facility.id}/`, data);
            }
            else {
                response = await API.post(`${BACKEND_HOST}/sites/facilities/`, data);
            }
            onSave(response.data);
            toast.success("시설 정보가 저장되었습니다.");
            toggle();
        }
        catch (error: any) {
            console.error("Save facility error:", error);
            let errorMessage = "시설 저장 중 오류가 발생했습니다.";
            if (error.response?.data) {
                const data = error.response.data;
                if (typeof data === 'object') {
                    const messages = Object.keys(data).map(key => {
                        const val = data[key];
                        return `${key}: ${Array.isArray(val) ? val.join(', ') : val}`;
                    });
                    if (messages.length > 0)
                        errorMessage = messages.join('\n');
                }
                else if (typeof data === 'string' && data.length < 100) {
                    errorMessage = data;
                }
            }
            toast.error(errorMessage);
        }
        finally {
            setIsSaving(false);
        }
    };
    return (<Modal isOpen={isOpen} onClose={toggle} size="2xl" title={facility && !isCopyMode ? "시설 정보 수정" : (isCopyMode ? "시설 정보 복사 등록" : "새 시설 등록")}>
            <ModalBody>
                <Form>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="facility-name">시설 이름 <span className="text-danger">*</span></Label>
                                <Input type="text" id="facility-name" name="name" value={formData.name} onChange={handleInputChange} placeholder="예: 실내 수영장, 바베큐장"/>
                            </FormGroup>
                            <FormGroup>
                                <Label htmlFor="facility-description">시설 설명</Label>
                                <Input type="textarea" id="facility-description" name="description" {...({ rows: 4 } as any)} value={formData.description} onChange={handleInputChange} placeholder="시설에 대한 간단한 설명을 입력하세요."/>
                            </FormGroup>
                            <FormGroup className="mb-3">
                                <Label>
                                    <Input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange}/>{" "}
                                    사용 가능 (활성화)
                                </Label>
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <Label>대표 이미지</Label>
                            <div className="mb-3 border-2 border-dashed border-gray-200 rounded-lg overflow-hidden h-48 flex flex-col items-center justify-center bg-gray-50 relative group">
                                {imagePreview ? (<>
                                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover"/>
                                        <div className="absolute top-2 right-2 flex gap-2">
                                            <Button color="light" size="sm" className="shadow-sm py-1 h-auto" onClick={() => document.getElementById("facility-image")?.click()}>
                                                변경
                                            </Button>
                                            <Button color="danger" size="sm" className="shadow-sm p-1.5 h-auto text-white bg-red-500 hover:bg-red-600 border-0" onClick={handleDeleteMainImage}>
                                                <Trash2 className="w-4 h-4"/>
                                            </Button>
                                        </div>
                                    </>) : (<div className="flex flex-col items-center cursor-pointer" onClick={() => document.getElementById("facility-image")?.click()}>
                                        <Upload size={32} className="text-gray-400 mb-2"/>
                                        <span className="text-sm text-gray-500">이미지 업로드</span>
                                    </div>)}
                                <input type="file" id="facility-image" className="hidden" accept="image/*" onChange={handleImageChange}/>
                            </div>
                        </Col>
                    </Row>

                    <hr className="my-4"/>

                    {/* Detail Images Section - Only show for existing facilities or created ones, but strict check here */}
                    {facility?.id && !isCopyMode && (<>
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <Label className="mb-0 font-bold">상세 이미지</Label>
                                        <small className="text-muted d-block">드래그하여 순서 변경 · 여러 장 동시 업로드 가능</small>
                                    </div>
                                    <div className="d-flex gap-2">
                                        {detailImages.length > 0 && (<Button color="soft-primary" size="sm" onClick={handleSaveDetailImageOrder}>
                                                순서 저장
                                            </Button>)}
                                        <Label htmlFor="detail-images-upload" className="btn btn-primary btn-sm mb-0 d-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
                                            <Plus size={16}/>
                                            이미지 추가
                                        </Label>
                                        <input type="file" id="detail-images-upload" className="d-none" accept="image/*" multiple onChange={handleDetailImagesUpload} disabled={uploadingImages}/>
                                    </div>
                                </div>

                                {/* Detail Images Grid */}
                                <div className="border rounded p-3 bg-light">
                                    {detailImages.length > 0 ? (<div className="d-flex flex-wrap gap-3">
                                            {detailImages.map((img, index) => (<div key={img.id} draggable onDragStart={() => handleDragStart(index)} onDragOver={(e) => handleDragOver(e, index)} onDragEnd={handleDragEnd} className={`position-relative border rounded overflow-hidden bg-white ${draggedIndex === index ? 'opacity-50' : ''}`} style={{ width: '120px', height: '120px', cursor: 'grab' }}>
                                                    <img src={img.image} alt={img.alt_text || `이미지 ${index + 1}`} className="w-100 h-100" style={{ objectFit: 'cover' }}/>
                                                    {/* Order Badge */}
                                                    <span className="position-absolute bg-dark text-white px-1 rounded-sm" style={{ top: '4px', left: '4px', fontSize: '10px' }}>
                                                        {index + 1}
                                                    </span>
                                                    {/* Delete Button */}
                                                    <Button color="danger" size="sm" className="position-absolute p-1" style={{ top: '4px', right: '4px', lineHeight: 1 }} onClick={() => handleDeleteDetailImage(img.id)}>
                                                        <X size={12}/>
                                                    </Button>
                                                    {/* Drag Handle */}
                                                    <div className="position-absolute bg-white/80 rounded-sm p-1" style={{ bottom: '4px', left: '4px' }}>
                                                        <GripVertical size={14} className="text-muted"/>
                                                    </div>
                                                </div>))}
                                        </div>) : (<div className="text-center text-muted py-4">
                                            <ImageIcon size={32} className="mb-2"/>
                                            <p className="mb-0">상세 이미지가 없습니다</p>
                                            <small>상단의 '이미지 추가' 버튼을 클릭하여 추가하세요</small>
                                        </div>)}
                                </div>

                                {uploadingImages && (<div className="text-center mt-2">
                                        <span className="text-primary">이미지 업로드 중...</span>
                                    </div>)}
                            </div>
                            <hr className="my-4"/>
                        </>)}

                    {/* Info notice for new facilities */}
                    {(!facility?.id || isCopyMode) && (<div className="alert alert-info mb-4">
                            <strong>참고:</strong> 상세 이미지는 시설을 저장한 후 추가할 수 있습니다.
                        </div>)}

                    <Row>
                        <Col md={4}>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="mb-0 font-bold">포함 사항</Label>
                                <Button color="soft-primary" size="sm" className="h-6 w-6 p-0 flex items-center justify-center" onClick={() => addArrayItem("include_info")}>
                                    <Plus size={14}/>
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {formData.include_info.map((item, idx) => (<div key={idx} className="flex gap-1">
                                        <Input bsSize="sm" value={item} onChange={(e) => handleArrayChange("include_info", idx, e.target.value)} placeholder="항목 입력"/>
                                        <Button color="soft-danger" size="sm" className="px-2" onClick={() => removeArrayItem("include_info", idx)}>
                                            <X size={14}/>
                                        </Button>
                                    </div>))}
                            </div>
                        </Col>

                        <Col md={4}>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="mb-0 font-bold">이용 안내</Label>
                                <Button color="soft-primary" size="sm" className="h-6 w-6 p-0 flex items-center justify-center" onClick={() => addArrayItem("using_info")}>
                                    <Plus size={14}/>
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {formData.using_info.map((item, idx) => (<div key={idx} className="flex gap-1">
                                        <Input bsSize="sm" value={item} onChange={(e) => handleArrayChange("using_info", idx, e.target.value)} placeholder="안내 사항"/>
                                        <Button color="soft-danger" size="sm" className="px-2" onClick={() => removeArrayItem("using_info", idx)}>
                                            <X size={14}/>
                                        </Button>
                                    </div>))}
                            </div>
                        </Col>

                        <Col md={4}>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="mb-0 font-bold">유의 사항</Label>
                                <Button color="soft-primary" size="sm" className="h-6 w-6 p-0 flex items-center justify-center" onClick={() => addArrayItem("caution_info")}>
                                    <Plus size={14}/>
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {formData.caution_info.map((item, idx) => (<div key={idx} className="flex gap-1">
                                        <Input bsSize="sm" value={item} onChange={(e) => handleArrayChange("caution_info", idx, e.target.value)} placeholder="주의 사항"/>
                                        <Button color="soft-danger" size="sm" className="px-2" onClick={() => removeArrayItem("caution_info", idx)}>
                                            <X size={14}/>
                                        </Button>
                                    </div>))}
                            </div>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={toggle} disabled={isSaving}>취소</Button>
                <Button color="success" onClick={handleSave} disabled={isSaving}>{isSaving ? "저장 중..." : "저장하기"}</Button>
            </ModalFooter>
        </Modal>);
};
export default FacilityModal;
